/* C2 (2026-07-17 steer): favicon 404s must degrade to the NEUTRAL non-letter mark
 * — never a broken <img>, never initials, never a placeholder letter (plan §1
 * decision 14). Every favicon in the app is fetched through the Google s2 proxy
 * (lib/format.ts favForDomain/faviconUrl), so a single delegated, capture-phase
 * error listener covers every render site (presence nodes, drawer, SERP rows,
 * register, matrix, AI citations, appendix, trend, wiki, profiles) with no
 * per-template retrofit.
 *
 * lit-safe: on error we only rewrite the failed <img>'s own `src` to a data-URI
 * of the neutral mark. lit tracks the value IT last committed (the favicon URL);
 * on a re-render with the same template value it sees "unchanged" and never
 * resets our src, so the fallback persists. A genuinely new favicon URL is set by
 * lit as usual and re-validated. The neutral SVG's colours are read from the live
 * design tokens at swap time — no raw hex in source, and it tracks light/dark.
 */

let cachedUri: string | null = null;

function neutralMarkDataUri(): string {
  // Re-read tokens each theme change is rare; cache once per session is fine, but
  // recompute if the cache is empty (tokens not yet applied at first call).
  if (cachedUri) return cachedUri;
  const cs = getComputedStyle(document.documentElement);
  const stroke = cs.getPropertyValue('--crm-border-strong').trim();
  const dot = cs.getPropertyValue('--crm-tertiary').trim();
  if (!stroke || !dot) return transparentPixel(); // tokens not ready — degrade blank, retry next error
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">` +
    `<rect x="4.5" y="4.5" width="15" height="15" rx="3.5" fill="none" stroke="${stroke}" stroke-width="1.4"/>` +
    `<circle cx="12" cy="12" r="3.1" fill="${dot}"/></svg>`;
  cachedUri = 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  return cachedUri;
}

function transparentPixel(): string {
  return 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
}

function isFaviconImg(el: EventTarget | null): el is HTMLImageElement {
  if (!(el instanceof HTMLImageElement)) return false;
  const src = el.getAttribute('src') || '';
  return src.includes('s2/favicons');
}

export function installFaviconFallback(): void {
  // Capture phase: <img> `error` events do not bubble.
  document.addEventListener(
    'error',
    (ev) => {
      const t = ev.target;
      if (!isFaviconImg(t)) return;
      t.classList.add('fav-neutral');
      t.src = neutralMarkDataUri();
    },
    true,
  );
}
